import { useEffect, useState } from "react";
import { useClientProjectsStore } from "../../../store/useClientProjectsStore";
import { MIcon, ModalShell, EmptyState, fmtDate } from "./shared";

/**
 * The full journey: where the project has been, where it is, and what is still
 * ahead. Stages come from the project's category, and their state is computed
 * from real milestones — a stage with no data behind it simply shows no dates
 * rather than inventing them.
 *
 * It stays available on completed projects as a historical record.
 */

const MARK = {
  completed: { icon: "check_circle", tone: "text-tertiary", ring: "bg-tertiary" },
  current: { icon: "radio_button_checked", tone: "text-primary", ring: "bg-primary" },
  upcoming: { icon: "radio_button_unchecked", tone: "text-outline", ring: "bg-outline-variant" },
} as const;

const ProjectLifecycleModal = ({
  projectId,
  onClose,
}: {
  projectId: string | null;
  onClose: () => void;
}) => {
  const fetchLifecycle = useClientProjectsStore((s) => s.fetchLifecycle);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setExpanded(null);
    fetchLifecycle(projectId).then((d) => {
      if (!cancelled) {
        setData(d);
        setExpanded(d?.currentStage || null);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  return (
    <ModalShell
      open={!!projectId}
      onClose={onClose}
      icon="route"
      title="Project Lifecycle"
      subtitle={data ? `${data.project.name}${data.project.category ? ` · ${data.project.category}` : ""}` : ""}
      width="max-w-2xl"
    >
      {loading && !data ? (
        <div className="py-xl text-center text-body-sm text-on-surface-variant">Loading the journey…</div>
      ) : !data ? (
        <EmptyState title="Lifecycle unavailable" detail="Please try again in a moment." />
      ) : (
        <ol className="relative">
          {data.stages.map((s: any, i: number) => {
            const mark = MARK[s.state as keyof typeof MARK] || MARK.upcoming;
            const isLast = i === data.stages.length - 1;
            const open = expanded === s.name;
            const hasDetail = s.expectedDate || s.completedDate || s.deliverable || s.clientAction;

            return (
              <li key={s.name} className="relative pl-9 pb-md last:pb-0">
                {/* Connector */}
                {!isLast && (
                  <span
                    className={`absolute left-[11px] top-6 bottom-0 w-0.5 ${
                      s.state === "completed" ? "bg-tertiary/40" : "bg-outline-variant"
                    }`}
                  />
                )}
                <span className={`absolute left-0 top-0.5 ${mark.tone}`}>
                  <MIcon name={mark.icon} className="text-[22px]" />
                </span>

                <button
                  onClick={() => hasDetail && setExpanded(open ? null : s.name)}
                  className={`w-full text-left ${hasDetail ? "" : "cursor-default"}`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span
                      className={`text-body-md font-semibold ${
                        s.state === "current"
                          ? "text-primary"
                          : s.state === "completed"
                          ? "text-on-surface"
                          : "text-on-surface-variant"
                      }`}
                    >
                      {s.name}
                    </span>
                    {s.state === "current" && (
                      <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-bold uppercase tracking-wider">
                        Current
                      </span>
                    )}
                  </div>

                  {/* One-line summary when collapsed */}
                  {!open && (s.completedDate || s.expectedDate) && (
                    <p className="text-[11px] text-on-surface-variant mt-0.5">
                      {s.state === "completed" && s.completedDate
                        ? `Completed ${fmtDate(s.completedDate)}`
                        : s.expectedDate
                        ? `Expected ${fmtDate(s.expectedDate)}`
                        : ""}
                    </p>
                  )}
                </button>

                {open && hasDetail && (
                  <div className="mt-2 p-md rounded-xl bg-surface-container-low border border-outline-variant/40 space-y-1.5">
                    {s.startDate && (
                      <p className="text-body-sm text-on-surface-variant">
                        Started: <b className="text-on-surface">{fmtDate(s.startDate)}</b>
                      </p>
                    )}
                    {s.completedDate && (
                      <p className="text-body-sm text-on-surface-variant">
                        Completed: <b className="text-on-surface">{fmtDate(s.completedDate)}</b>
                      </p>
                    )}
                    {!s.completedDate && s.expectedDate && (
                      <p className="text-body-sm text-on-surface-variant">
                        Expected completion: <b className="text-on-surface">{fmtDate(s.expectedDate)}</b>
                      </p>
                    )}
                    {s.deliverable && (
                      <p className="text-body-sm text-on-surface-variant">
                        Deliverable: <b className="text-on-surface">{s.deliverable}</b>
                      </p>
                    )}
                    {s.clientAction && (
                      <p className="text-body-sm text-error font-semibold flex items-center gap-1.5">
                        <MIcon name="flag" className="text-[16px]" />
                        {s.clientAction.title}
                        {s.clientAction.dueDate ? ` · due ${fmtDate(s.clientAction.dueDate)}` : ""}
                      </p>
                    )}
                    {s.next && (
                      <p className="text-body-sm text-on-surface-variant">
                        Next: <b className="text-on-surface">{s.next}</b>
                      </p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </ModalShell>
  );
};

export default ProjectLifecycleModal;
